import { createSelector, createSlice } from "@reduxjs/toolkit"
import { RootState } from "../../../../app/store"
import { deleteDocSection, loadDocument } from "../../documentsApi"
import { sectionSelectors } from "../../documentSlice"
import { FormDefinition } from "./formSchema"
import {
    createFormDefinition, createFormSection, deleteFormDefinition, loadDocumentForms, LoadedFormSection, loadFormDefinition,
    loadFormDefinitions, setFormDefinitionStatus, setFormFieldValue, updateFormDefinition,
} from "./formSectionApi"

/**
 * A cache of what the API holds: the company's form library and the FORM sections of the documents
 * opened so far. Field values are written optimistically; a failed write reloads the document's forms.
 */
export type FormLoadState = 'empty' | 'loading' | 'loaded' | 'error'

interface FormSectionState {
    definitions: FormDefinition[]
    definitionsState: FormLoadState
    /** Keyed by section id. */
    sections: Record<string, LoadedFormSection>
    /** Keyed by document id. */
    documents: Record<string, FormLoadState>
}

const initialState: FormSectionState = {
    definitions: [],
    definitionsState: 'empty',
    sections: {},
    documents: {},
}

const upsertDefinition = (state: FormSectionState, definition: FormDefinition) => {
    const index = state.definitions.findIndex(d => d.id === definition.id)
    if (index >= 0) state.definitions[index] = definition
    else state.definitions.push(definition)
}

const formSectionSlice = createSlice({
    name: 'formSection',
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder.addCase(loadFormDefinitions.pending, state => {
            state.definitionsState = 'loading'
        })
        builder.addCase(loadFormDefinitions.fulfilled, (state, action) => {
            state.definitions = action.payload
            state.definitionsState = 'loaded'
        })
        builder.addCase(loadFormDefinitions.rejected, state => {
            state.definitionsState = 'error'
        })
        for (const thunk of [loadFormDefinition, createFormDefinition, updateFormDefinition, setFormDefinitionStatus]) {
            builder.addCase(thunk.fulfilled, (state, action) => upsertDefinition(state, action.payload))
        }
        builder.addCase(deleteFormDefinition.fulfilled, (state, action) => {
            state.definitions = state.definitions.filter(d => d.id !== action.payload)
        })

        // (Re)opening a document refetches its forms on first use, so values others saved show up.
        builder.addCase(loadDocument.pending, (state, action) => {
            delete state.documents[String(action.meta.arg)]
        })
        builder.addCase(loadDocumentForms.pending, (state, action) => {
            state.documents[String(action.meta.arg)] = 'loading'
        })
        builder.addCase(loadDocumentForms.fulfilled, (state, action) => {
            const documentId = String(action.meta.arg)
            for (const [sectionId, form] of Object.entries(state.sections)) {
                if (form.documentId === documentId) delete state.sections[sectionId]
            }
            for (const form of action.payload) state.sections[form.sectionId] = form
            state.documents[documentId] = 'loaded'
        })
        builder.addCase(loadDocumentForms.rejected, (state, action) => {
            state.documents[String(action.meta.arg)] = 'error'
        })
        builder.addCase(createFormSection.fulfilled, (state, action) => {
            const { form } = action.payload
            state.sections[form.sectionId] = form
        })
        builder.addCase(setFormFieldValue.pending, (state, action) => {
            const { sectionId, key, value } = action.meta.arg
            const section = state.sections[sectionId]
            if (section) section.values[key] = value
        })
        // Only the written key: an earlier write finishing late must not undo a later one's optimism.
        builder.addCase(setFormFieldValue.fulfilled, (state, action) => {
            const { sectionId, key } = action.meta.arg
            const section = state.sections[sectionId]
            if (!section) return
            if (key in action.payload.values) section.values[key] = action.payload.values[key]
            else delete section.values[key]
        })
        // The server returns the document's remaining sections, which also drops the subsections of a
        // deleted group, so anything of this document missing from that list is gone.
        builder.addCase(deleteDocSection.fulfilled, (state, action) => {
            const { documentId } = action.meta.arg
            const remaining = new Set(action.payload.sections.map(s => String(s.id)))
            for (const [sectionId, data] of Object.entries(state.sections)) {
                if (data.documentId === String(documentId) && !remaining.has(sectionId)) {
                    delete state.sections[sectionId]
                }
            }
        })
    },
})

export const selectFormDefinitions = (state: RootState) => state.formSection.definitions

export const selectFormDefinitionsState = (state: RootState) => state.formSection.definitionsState

export const selectFormDefinition = (state: RootState, id: string) =>
    state.formSection.definitions.find(d => d.id === id)

export const selectPublishedFormDefinitions = createSelector(
    selectFormDefinitions,
    definitions => definitions.filter(d => d.status === 'PUBLISHED'),
)

export const selectFormSection = (state: RootState, sectionId: string): LoadedFormSection | undefined =>
    state.formSection.sections[sectionId]

export const selectDocumentFormsState = (state: RootState, documentId: string | number): FormLoadState =>
    state.formSection.documents[String(documentId)] ?? 'empty'

/** Definition id → the section using it, for the sections of the open document. */
export const selectUsedFormDefinitions = createSelector(
    (state: RootState) => state.formSection.sections,
    (state: RootState) => sectionSelectors.selectEntities(state),
    (forms, docSections) => {
        const used = new Map<string, { sectionId: string, numeration: string, name: string }>()
        for (const form of Object.values(forms)) {
            const section = docSections[form.sectionId]
            if (section) {
                used.set(form.definitionId, { sectionId: section.id, numeration: section.numeration, name: section.name })
            }
        }
        return used
    },
)

export default formSectionSlice.reducer
